import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

export default function About() {
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
  const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';
  const companyAddress = process.env.REACT_APP_COMPANY_ADDRESS || 'Harare, Zimbabwe';

  const values = [
    { icon: '🎯', title: 'Customer First', desc: 'Every feature we build starts with one question: does this make life easier for our customers?' },
    { icon: '⚡', title: 'Move Fast', desc: 'The business world moves quickly. We ship improvements constantly so you always have the best tools.' },
    { icon: '🤝', title: 'Built for Zimbabwe', desc: 'We understand the local market — EcoCash, load shedding, bandwidth constraints and all.' },
    { icon: '🔒', title: 'Reliable & Secure', desc: 'Your customer data and conversations are encrypted and never shared with third parties.' },
  ];

  const stats = [
    { num: '500+', label: 'Businesses using autopilot' },
    { num: '100k+', label: 'Conversations handled' },
    { num: '2024', label: 'Founded in Harare' },
    { num: '24/7', label: 'Uptime guarantee' },
  ];

  const team = [
    { name: 'Tino Moyo', role: 'CEO & Co-founder', emoji: '👨🏾‍💻' },
    { name: 'Rudo Chikwanda', role: 'CTO', emoji: '👩🏾‍💻' },
    { name: 'Farai Mutasa', role: 'Head of Growth', emoji: '👨🏾‍📊' },
  ];

  return (
    <>
      <SEO
        title={`About | ${companyName}`}
        description={`Learn about ${companyName}'s mission to help Zimbabwean businesses automate WhatsApp communication and provide exceptional customer service.`}
        keywords="about autopilot, WhatsApp bot company, Zimbabwe tech startup, customer service automation"
        type="website"
      />

      <style>{`
        * { box-sizing: border-box; }
        .about-hero { padding: 120px 24px 72px; }
        .about-section { padding: 72px 24px; }
        .stats-grid { grid-template-columns: repeat(4, 1fr); }
        .values-grid { grid-template-columns: repeat(2, 1fr); }
        .team-grid { grid-template-columns: repeat(3, 1fr); }

        @media (max-width: 768px) {
          .about-hero { padding: 100px 20px 56px; }
          .about-section { padding: 52px 20px; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .values-grid { grid-template-columns: 1fr; }
          .team-grid { grid-template-columns: 1fr; max-width: 320px; margin-left: auto; margin-right: auto; }
          h1 { font-size: 36px !important; }
          h2 { font-size: 26px !important; }
        }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0a0914', color: 'white', minHeight: '100vh' }}>

        {/* ── NAV ── */}
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 32px', height: '60px', background: 'rgba(10,9,20,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Home</Link>
            <Link to="/about" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>About</Link>
            <Link to="/pricing" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Pricing</Link>
            <Link to="/documentation" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Docs</Link>
               <Link to="/contact" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Contact</Link>
            <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '20px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>Get started</Link>
          </div>
        </nav>

        {/* ── HERO ── */}
        <div className="about-hero" style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: 'rgba(91,79,232,0.18)', border: '1px solid rgba(91,79,232,0.35)', color: '#a5b4fc', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '20px' }}>
            OUR STORY
          </div>
          <h1 style={{ fontSize: '52px', fontWeight: '800', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
            We built the bot we<br />
            <span style={{ color: '#818cf8' }}>wished we had</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '17px', lineHeight: 1.75, margin: 0 }}>
            In 2024, we watched business owners in Harare spend hours every day answering the same WhatsApp messages. We knew there was a better way — so we built it.
          </p>
        </div>

        {/* ── HERO IMAGE ── */}
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 24px 72px' }}>
          <div style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', aspectRatio: '16/7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src="/ai_image.webp"
              alt={`${companyName} team`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => {
                // Fallback if image missing
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* ── STATS ── */}
        <div style={{ background: 'rgba(91,79,232,0.06)', borderTop: '1px solid rgba(91,79,232,0.15)', borderBottom: '1px solid rgba(91,79,232,0.15)' }}>
          <div style={{ maxWidth: '960px', margin: '0 auto', padding: '48px 24px' }}>
            <div className="stats-grid" style={{ display: 'grid', gap: '16px' }}>
              {stats.map((s, i) => (
                <div key={i} style={{ textAlign: 'center', padding: '24px 16px' }}>
                  <div style={{ fontSize: '38px', fontWeight: '800', letterSpacing: '-0.02em', color: 'white', marginBottom: '6px' }}>{s.num}</div>
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── MISSION ── */}
        <div className="about-section" style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>Our Mission</div>
              <h2 style={{ fontSize: '32px', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 18px', lineHeight: 1.2 }}>
                Every business deserves to be always open
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px', lineHeight: 1.75, margin: '0 0 16px' }}>
                Most small businesses in Zimbabwe are run by one or two people who wear every hat. They can't afford to miss a customer — but they also can't be glued to their phones 24 hours a day.
              </p>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px', lineHeight: 1.75, margin: 0 }}>
                {companyName} gives those businesses a tireless digital assistant that answers questions, captures leads, takes bookings, and sends updates — automatically, while the owner sleeps, eats, or does the actual work.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: '💬', text: 'Instant replies to any WhatsApp message, day or night' },
                { icon: '📅', text: 'Automated booking flows that replace phone tag' },
                { icon: '📊', text: 'Analytics that show you exactly what customers ask' },
                { icon: '🇿🇼', text: 'EcoCash & OneMoney payment support built in' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: 1.5 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── VALUES ── */}
        <div className="about-section" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth: '960px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>What We Stand For</div>
              <h2 style={{ fontSize: '32px', fontWeight: '800', letterSpacing: '-0.02em', margin: 0 }}>Our values</h2>
            </div>
            <div className="values-grid" style={{ display: 'grid', gap: '16px' }}>
              {values.map((v, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '28px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '14px' }}>{v.icon}</div>
                  <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px' }}>{v.title}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.65 }}>{v.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── TEAM ── */}
        <div className="about-section" style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>The People</div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 10px' }}>Built by Zimbabweans</h2>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '15px', maxWidth: '440px', margin: '0 auto' }}>
              We're a small team based in Harare. We use the same tools we build.
            </p>
          </div>
          {/* <div className="team-grid" style={{ display: 'grid', gap: '16px' }}>
            {team.map((member, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '32px 24px', textAlign: 'center' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(91,79,232,0.2)', border: '2px solid rgba(91,79,232,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', margin: '0 auto 16px' }}>
                  {member.emoji}
                </div>
                <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>{member.name}</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>{member.role}</div>
              </div>
            ))}
          </div> */}
        </div>

        {/* ── CONTACT ── */}
        <div className="about-section" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>👋</div>
            <h2 style={{ fontSize: '30px', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 12px' }}>Come say hello</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: 1.7, margin: '0 0 28px' }}>
              We're always happy to chat — whether you have a question about the product, want to partner with us, or just want to say hi.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px' }}>
              <a href={`mailto:${companyEmail}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#5B4FE8', color: 'white', padding: '12px 24px', borderRadius: '12px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
                📧 {companyEmail}
              </a>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', padding: '12px 24px', borderRadius: '12px', fontSize: '14px' }}>
                📍 {companyAddress}
              </div>
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="about-section" style={{ background: 'linear-gradient(135deg,#1a1040 0%,#0c0820 100%)', textAlign: 'center' }}>
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '30px', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
              Ready to put your business on autopilot?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', margin: '0 0 28px', lineHeight: 1.6 }}>
              Start free. No credit card, no tech skills needed.
            </p>
            <Link to="/register" style={{ display: 'inline-block', background: '#5B4FE8', color: 'white', padding: '14px 32px', borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '700', boxShadow: '0 0 40px rgba(91,79,232,0.35)' }}>
              Get started free →
            </Link>
          </div>
        </div>

      </div>
    </>
  );
}